import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import api from '../../api/api';
import { showConfirm } from '../../utils/confirmUtils';
import { toastError, toastSuccess } from '../../utils/toasts';
import { Card, Button, Badge } from '../../components/ui';
import * as XLSX from 'xlsx';
import TableToolbar from '../../components/stock/TableToolbar';

const initialForm = {
  name: '', description: '', sku: '', barcode: '', reference: '',
  categoryId: '', unit: 'pcs', purchasePrice: '', sellingPrice: '',
  currentQuantity: '', minQuantity: '',
};

const toolbarColumns = [
  { label: 'Nom', accessor: 'name' },
  { label: 'SKU', accessor: 'sku' },
  { label: 'Référence', accessor: 'reference' },
  { label: 'Catégorie', accessor: 'category' },
  { label: 'Unité', accessor: 'unit' },
  { label: 'Qté', accessor: 'qty' },
  { label: 'Min', accessor: 'min' },
  { label: 'Prix achat', accessor: 'purchasePrice' },
  { label: 'Prix vente', accessor: 'sellingPrice' },
  { label: 'Statut', accessor: 'status' },
];

export default function StockItems() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (search) params.q = search;
      const res = await api.get('/stock/items', { params });
      if (res.data.ok) setItems(res.data.items);
    } catch (err) {
      console.error('Erreur chargement articles:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/stock/categories');
      if (res.data.ok) setCategories(res.data.categories);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [fetchItems, fetchCategories]);

  const openCreate = () => {
    setEditItem(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      description: item.description || '',
      sku: item.sku || '',
      barcode: item.barcode || '',
      reference: item.reference || '',
      categoryId: item.categoryId?._id || item.categoryId || '',
      unit: item.unit || 'pcs',
      purchasePrice: String(item.purchasePrice ?? ''),
      sellingPrice: String(item.sellingPrice ?? ''),
      currentQuantity: String(item.currentQuantity ?? ''),
      minQuantity: String(item.minQuantity ?? ''),
    });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      purchasePrice: parseFloat(form.purchasePrice) || 0,
      sellingPrice: parseFloat(form.sellingPrice) || 0,
      currentQuantity: parseInt(form.currentQuantity, 10) || 0,
      minQuantity: parseInt(form.minQuantity, 10) || 0,
    };
    try {
      if (editItem) {
        const res = await api.put(`/stock/items/${editItem._id}`, payload);
        if (res.data.ok) {
          setItems((prev) => prev.map((i) => (i._id === editItem._id ? res.data.item : i)));
        }
      } else {
        const res = await api.post('/stock/items', payload);
        if (res.data.ok) {
          setItems((prev) => [res.data.item, ...prev]);
        }
      }
      setShowForm(false);
      setEditItem(null);
    } catch (err) {
      console.error('Erreur sauvegarde article:', err);
      toastError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('Supprimer définitivement cet article ?')) return;
    try {
      await api.delete(`/stock/items/${id}`);
      setItems((prev) => prev.filter((i) => i._id !== id));
    } catch (err) {
      console.error('Erreur suppression article:', err);
      toastError('Erreur lors de la suppression');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      let imported = 0;
      for (const row of rows) {
        const payload = {
          name: row['Nom'] || row['name'] || '',
          sku: row['SKU'] || row['sku'] || '',
          reference: row['Référence'] || row['reference'] || '',
          description: row['Description'] || row['description'] || '',
          unit: row['Unité'] || row['unit'] || 'pcs',
          purchasePrice: parseFloat(row['Prix achat'] || row['purchasePrice'] || 0),
          sellingPrice: parseFloat(row['Prix vente'] || row['sellingPrice'] || 0),
          currentQuantity: parseInt(row['Qté'] || row['currentQuantity'] || 0, 10),
          minQuantity: parseInt(row['Stock min'] || row['minQuantity'] || 0, 10),
        };
        if (!payload.name) continue;
        const res = await api.post('/stock/items', payload);
        if (res.data.ok) imported++;
      }
      toastSuccess(`${imported} article(s) importé(s) avec succès`);
      fetchItems();
    } catch (err) {
      console.error('Erreur import:', err);
      toastError("Erreur lors de l'import: " + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const exportData = useMemo(() => items.map((i) => ({
    name: i.name,
    sku: i.sku || '-',
    reference: i.reference || '-',
    category: i.categoryId?.name || '-',
    unit: i.unit,
    qty: i.currentQuantity,
    min: i.minQuantity,
    purchasePrice: i.purchasePrice,
    sellingPrice: i.sellingPrice,
    status: i.status === 'active' ? 'Actif' : 'Inactif',
  })), [items]);

  if (loading) return <p className="text-gray-500 p-6">Chargement...</p>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Articles</h1>
          <Button variant="primary" onClick={openCreate}>Nouvel article</Button>
        </div>

        <TableToolbar
          title="Articles stock"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Rechercher par nom, SKU ou référence..."
          columns={toolbarColumns}
          data={exportData}
          filename="articles_stock"
          extraActions={
            <>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImport}
                ref={fileInputRef}
                className="hidden"
              />
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Import...' : 'Import Excel'}
              </Button>
            </>
          }
        />

        {showForm && (
          <Card className="mb-6 p-4">
            <h2 className="text-lg font-semibold mb-4">{editItem ? 'Modifier' : 'Nouvel'} article</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} required className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">SKU</label>
                <input type="text" name="sku" value={form.sku} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Code-barres</label>
                <input type="text" name="barcode" value={form.barcode} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Catégorie</label>
                <select name="categoryId" value={form.categoryId} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">Sélectionner...</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Unité</label>
                <select name="unit" value={form.unit} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="pcs">Pièce</option>
                  <option value="kg">Kilogramme</option>
                  <option value="g">Gramme</option>
                  <option value="l">Litre</option>
                  <option value="ml">Millilitre</option>
                  <option value="m">Mètre</option>
                  <option value="m2">Mètre carré</option>
                  <option value="box">Boîte</option>
                  <option value="pack">Pack</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Référence</label>
                <input type="text" name="reference" value={form.reference} onChange={handleChange} placeholder="ex: REF-001" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Prix d'achat</label>
                <input type="text" inputMode="decimal" name="purchasePrice" value={form.purchasePrice} onChange={handleChange} placeholder="0.000" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Prix de vente</label>
                <input type="text" inputMode="decimal" name="sellingPrice" value={form.sellingPrice} onChange={handleChange} placeholder="0.000" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Qté initiale</label>
                <input type="text" inputMode="numeric" name="currentQuantity" value={form.currentQuantity} onChange={handleChange} disabled={!!editItem} placeholder="0" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Stock minimum</label>
                <input type="text" inputMode="numeric" name="minQuantity" value={form.minQuantity} onChange={handleChange} placeholder="0" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={2} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-3 flex gap-2">
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Sauvegarde...' : editItem ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button variant="secondary" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </form>
          </Card>
        )}

        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Nom</th>
                  <th className="text-left px-4 py-2">SKU</th>
                  <th className="text-left px-4 py-2">Réf.</th>
                  <th className="text-left px-4 py-2">Catégorie</th>
                  <th className="text-left px-4 py-2">Unité</th>
                  <th className="text-right px-4 py-2">Qté</th>
                  <th className="text-right px-4 py-2">Min</th>
                  <th className="text-right px-4 py-2">Prix achat</th>
                  <th className="text-right px-4 py-2">Prix vente</th>
                  <th className="text-center px-4 py-2">Statut</th>
                  <th className="text-center px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                  {items.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-4 text-gray-500">Aucun article</td></tr>
                )}
                {items.map((item) => {
                  const isLow = item.minQuantity > 0 && item.currentQuantity <= item.minQuantity;
                  const isOut = item.currentQuantity <= 0;
                  return (
                    <tr key={item._id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{item.name}</td>
                      <td className="px-4 py-2 text-gray-600">{item.sku || '-'}</td>
                      <td className="px-4 py-2 text-gray-600">{item.reference || '-'}</td>
                      <td className="px-4 py-2">{item.categoryId?.name || '-'}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                      <td className={`px-4 py-2 text-right font-bold ${isOut ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-gray-900'}`}>
                        {item.currentQuantity}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">{item.minQuantity}</td>
                      <td className="px-4 py-2 text-right">{item.purchasePrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}</td>
                      <td className="px-4 py-2 text-right">{item.sellingPrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}</td>
                      <td className="px-4 py-2 text-center">
                        <Badge variant={item.status === 'active' ? 'success' : 'gray'}>
                          {item.status === 'active' ? 'Actif' : 'Inactif'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="secondary" size="sm" onClick={() => openEdit(item)}>Modifier</Button>
                          <Button variant="danger" size="sm" onClick={() => handleDelete(item._id)}>Suppr.</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}